// StatisticsModal.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useStatistics } from './StatisticsContext'; // Import the useStatistics hook

const Overlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    padding: 20px;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    position: relative;
    box-sizing: border-box;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.text};
    font-size: 1.5rem;
    cursor: pointer;
    position: absolute;
    top: 16px;
    right: 16px;

    &:hover {
        color: ${({ theme }) => theme.accentColor};
    }
`;

const Title = styled.h2`
    margin-top: 0;
    text-align: center;
`;

const StatsGrid = styled.div`
    display: flex;
    justify-content: space-around;
    margin-top: 20px;
`;

const StatBlock = styled.div`
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: 2rem;
    font-weight: bold;
`;

const StatLabel = styled.div`
    font-size: 0.9rem;
`;

const BarGraphContainer = styled.div`
    margin-top: 20px;
`;

const BarRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
`;

const BarLabel = styled.div`
    width: 20px;
`;

const Bar = styled.div<{ width: number; isHighlight: boolean }>`
    background-color: ${({ theme, isHighlight }) =>
            isHighlight ? theme.accentColor : theme.borderColor};
    height: 24px;
    width: ${({ width }) => width}%;
    margin-left: 8px;
    border-radius: 4px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;
    color: ${({ theme }) => theme.text};
    font-weight: bold;
`;

interface StatisticsModalProps {
    onClose: () => void;
}

const StatisticsModal: React.FC<StatisticsModalProps> = ({ onClose }) => {
    const { statistics } = useStatistics(); // Get statistics from context
    const maxGuessCount = Math.max(...statistics.guessesDistribution);

    return (
        <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <ModalContainer
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
            >
                <CloseButton onClick={onClose} aria-label="Close">
                    &times;
                </CloseButton>
                <Title>Statistics</Title>
                <StatsGrid>
                    <StatBlock>
                        <StatNumber>{statistics.gamesPlayed}</StatNumber>
                        <StatLabel>Played</StatLabel>
                    </StatBlock>
                    <StatBlock>
                        <StatNumber>{statistics.winRate}</StatNumber>
                        <StatLabel>Win %</StatLabel>
                    </StatBlock>
                    <StatBlock>
                        <StatNumber>{statistics.currentStreak}</StatNumber>
                        <StatLabel>
                            Current
                            <br />
                            Streak
                        </StatLabel>
                    </StatBlock>
                    <StatBlock>
                        <StatNumber>{statistics.maxStreak}</StatNumber>
                        <StatLabel>
                            Max
                            <br />
                            Streak
                        </StatLabel>
                    </StatBlock>
                </StatsGrid>
                <BarGraphContainer>
                    <h3>Guess Distribution</h3>
                    {statistics.guessesDistribution.map((count, index) => (
                        <BarRow key={index}>
                            <BarLabel>{index + 1}</BarLabel>
                            <Bar
                                width={maxGuessCount > 0 ? (count / maxGuessCount) * 100 : 0}
                                isHighlight={count === maxGuessCount}
                            >
                                {count}
                            </Bar>
                        </BarRow>
                    ))}
                </BarGraphContainer>
            </ModalContainer>
        </Overlay>
    );
};

export default StatisticsModal;
